import { Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useBiology } from '../../../contexts/PlanOperationContext/Biology';
import { selectOneSetting, selectPermissions, selectTenant } from '../../../util/store/selectors';
import { INTEGRATION_MODE, KEY_TAB } from '../../../util/varibles/constants';
import { ACTION } from '../../../util/varibles/permission';
import { PlanSetting } from '../../../util/varibles/userSetting';
import { loginActions } from '../../LoginPage/reducer';
import { selectPlan } from '../constants';
import NavUnderline from './_components/NavUnderline';
import { isShowTab } from './index';
import { isPast } from './ServiceRequest/Card';
import styles from './style.module.scss';

const ServiceRequest = () => {
    const serviceRequests = useSelector(selectPlan.serviceRequests) || [];
    const { new_request, old_request } = serviceRequests.reduce((rs: { new_request: number, old_request: number }, item) => {
        const { operations, vessel_resources } = item;
        if (isPast(item)) {
            rs.old_request++;
        } else if (operations?.length === 0 && vessel_resources?.length === 0) {
            rs.new_request++;
        }
        return rs;
    }, { new_request: 0, old_request: 0 })
    return <div className="flex gap-1 justify-center">
        <span>Request</span>
        <Tooltip
            title={`There ${new_request > 1 ? 'are' : 'is'} ${new_request} unplanned ${new_request > 1 ? 'requests' : 'request'}`}
        >
            ({new_request})
        </Tooltip>
        {old_request > 0 && (
            <Tooltip
                title={`There ${old_request > 1 ? 'are' : 'is'} ${old_request} overdue unplanned ${old_request > 1 ? 'requests' : 'request'}`}
            >
                <span className="color-danger">({old_request})</span>
            </Tooltip>
        )}
    </div >
}

const Cancel = () => {
    const canceled = useSelector(selectPlan.canceled);
    const count = canceled.filter(({ isShow = true }: any) => isShow);
    return <div>
        Canceled operation {count.length > 0 ? `(${count.length})` : ''}
    </div>
}


const Header: React.FC = () => {
    const planStoreTabs = useSelector(selectOneSetting(PlanSetting.BIOLOGY_TAB))
    const permission = useSelector(selectPermissions([
        ACTION.PLAN_OP.BIOLOGY_TAB,
        ACTION.PLAN_OP.LOCATION_TAB,
        ACTION.PLAN_OP.SERVICE_REQUEST_TAB,
        ACTION.PLAN_OP.STHP_TAB,
        ACTION.PLAN_OP.SMOLT_TAB,
        ACTION.PLAN_OP.CANCEL_TAB
    ]))
    const tenant = useSelector(selectTenant);
    const [smoltSites] = useBiology(state => state.smoltSites);
    const { planning_tabs = [] } = tenant;
    const filterTabs = useMemo(() => new Set(planning_tabs), [planning_tabs]);
    const { type = '' } = tenant?.integration || {};
    const dispatch = useDispatch();
    const root = useRef(null);

    const { header, visibleTabs } = useMemo(() => {
        const header = [
            {
                id: KEY_TAB.PRODUCTION,
                name: 'Biology',
                width: '165px',
                visible: permission[0] && isShowTab(KEY_TAB.PRODUCTION, filterTabs)
            },
            {
                id: KEY_TAB.LOCATIONS,
                name: 'Site/Location',
                width: '165px',
                visible: permission[1] && isShowTab(KEY_TAB.LOCATIONS, filterTabs)
            },
            {
                id: KEY_TAB.SERVICE_REQUEST,
                name: <ServiceRequest />,
                width: '165px',
                visible: permission[2] && isShowTab(KEY_TAB.SERVICE_REQUEST, filterTabs)
            },
            {
                id: KEY_TAB.HARVEST,
                name: 'STHP',
                width: '125px',
                visible: permission[3] && type === INTEGRATION_MODE.STHP_FILE && isShowTab(KEY_TAB.PRODUCTION, filterTabs)
            },
            {
                id: KEY_TAB.SMOLT,
                name: 'Smolt',
                width: '130px',
                visible: permission[4] && smoltSites.length > 0 && isShowTab(KEY_TAB.PRODUCTION, filterTabs)
            },
            {
                id: KEY_TAB.CANCEL,
                name: <Cancel />,
                width: '180px',
                visible: permission[5]
            },
        ];
        const visibleTabs = header.filter(item => item.visible).map(item => item.id);
        return { header, visibleTabs }
    }, [permission, smoltSites.length, type, filterTabs])

    const tab = useMemo(() => {
        const ids = new Set(visibleTabs);
        switch (planStoreTabs) {
            case KEY_TAB.SMOLT: {
                if (smoltSites.length === 0)
                    return KEY_TAB.PRODUCTION
                return planStoreTabs
            }
            default: {
                if (ids.has(planStoreTabs))
                    return planStoreTabs
                return visibleTabs[0]
            }
        }
    }, [planStoreTabs, smoltSites.length, visibleTabs]);

    useEffect(() => {
        setTimeout(() => {
            const el = document.getElementById('container-tabs');
            if (el) {
                const { childNodes } = el;
                childNodes.forEach((item: any) => {
                    const { id } = item;
                    item.dataset.active = id === tab
                })
            }
        }, 230)
    }, [tab]);

    const handleChangeTab = useCallback((value: KEY_TAB) => {
        dispatch(loginActions.updateUserSetting([{ planStoreTabs: value }]));
    }, [dispatch]);

    return <div ref={root} className={styles['tabs-control']} data-tab={tab}>
        {header.map(({ id, name, width, visible }: any) => visible && <div key={id} {...{
            id: 'tab-' + id,
            className: styles['tabs-nav'],
            'data-active': id === tab,
            'data-tab': id,
            onClick: () => id === tab ? null : handleChangeTab(id),
            style: { width }
        }}>
            {name}
        </div>)}
        <NavUnderline target={tab} />
    </div>;
};

export default Header;

